import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { motion } from 'framer-motion';
import apiManager from '../../../lib/api';
import trackingManager from '../../../lib/tracking';
import { EventType } from '../../../lib/tracking/eventTracker';
import { T } from '@tolgee/react';
import { OrderHistoryEntry } from '../../../lib/api/models';

interface OrderItem {
  id: string;
  productId: string;
  name: string;
  variant: string;
  price: number;
  priceBeforeDiscount?: number;
  discountInfo?: {
    type: string;
    value: number;
    displayValue: string;
  };
  discountSource?: 'user' | 'global';
  priceBeforeGlobalDiscount?: number;
  globalDiscountInfo?: {
    name: string;
    displayValue: string;
  };
  quantity: number;
  image: string;
}

interface OrderDetail {
  id: string;
  date: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'payment_failed' | 'refunded';
  total: number;
  subtotal: number;
  shipping: number;
  items: OrderItem[];
  shippingAddress: {
    name: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
  };
  paymentMethod: string;
  trackingNumber?: string;
  trackingUrl?: string;
  estimatedDelivery?: string;
  history?: OrderHistoryEntry[];
  prontoPagoDiscountAtOrderTime?: number;
  notes?: string;
  dropShipping?: {
    enabled: boolean;
    notifyRecipient: boolean;
    recipient?: {
      name: string;
      email?: string;
      phone?: string;
      notes?: string;
      address?: {
        street?: string;
        city?: string;
        postalCode?: string;
        country?: string;
      };
    };
  };
}

const OrderDetailPage = () => {
  const { orderId, id } = useParams<{ orderId?: string; id?: string }>();
  const { auth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const resolvedOrderId = orderId || id;
  const backToOrders = location.pathname.startsWith('/commercial/')
    ? '/commercial/orders'
    : location.pathname.startsWith('/distributor/')
      ? '/distributor/orders'
      : '/orders';

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Cargar detalles del pedido usando la API
  useEffect(() => {
    if (auth.user && resolvedOrderId) {
      const fetchOrderDetail = async () => {
        setIsLoading(true);
        try {
          // Registrar evento de vista del detalle de pedido
          trackingManager.events.track(
            EventType.PAGE_VIEW,
            {
              path: window.location.pathname,
              pageTitle: document.title,
              customData: { section: 'order_detail', orderId: resolvedOrderId }
            }
          );

          // Rastrear evento personalizado
          trackingManager.events.track(
            EventType.CUSTOM,
            {
              eventName: 'view_order_detail',
              userId: auth.user?.id,
              orderId: resolvedOrderId
            }
          );

          // Usar la API para obtener los detalles del pedido
          const response = await apiManager.orders.getOrder(resolvedOrderId);

          if (response.success && response.data) {
            const orderData = response.data as any;

            // Transformar los datos de la API al formato que espera el componente
            const formattedOrder: OrderDetail = {
              id: orderData.id,
              date: orderData.createdAt,
              status: orderData.status,
              total: orderData.totalAmount,
              subtotal: orderData.totalAmount - (orderData.shippingCost || 0),
              shipping: orderData.shippingCost || 0,
              items: orderData.products.map((item: any) => ({
                id: item.id || `item_${Math.random().toString(36).substr(2, 9)}`,
                productId: typeof item.product === 'string' ? item.product : item.product.id,
                name: item.name,
                variant: '',
                price: item.price,
                priceBeforeDiscount: item.priceBeforeDiscount || undefined,
                discountInfo: item.discountInfo || undefined,
                discountSource: item.discountSource || undefined,
                priceBeforeGlobalDiscount: item.priceBeforeGlobalDiscount || undefined,
                globalDiscountInfo: item.globalDiscountInfo || undefined,
                quantity: item.quantity,
                image: typeof item.product !== 'string' && item.product.images && item.product.images.length > 0
                  ? item.product.images[0].url
                  : '/placeholder-product.jpg'
              })),
              shippingAddress: {
                name: orderData.dropShipping?.recipient?.name || orderData.customerInfo?.name || orderData.customer?.name || auth.user?.name || '',
                address: orderData.shippingAddress.street,
                city: orderData.shippingAddress.city,
                postalCode: orderData.shippingAddress.postalCode,
                country: orderData.shippingAddress.country
              },
              paymentMethod: orderData.paymentMethod === 'card' ? 'Tarjeta de crédito' :
                orderData.paymentMethod === 'bank_transfer' ? 'Transferencia bancaria' :
                  orderData.paymentMethod === 'paypal' ? 'PayPal' :
                    orderData.paymentMethod === 'cash' ? 'Efectivo' :
                      orderData.paymentMethod === 'stripe' ? 'Stripe' : orderData.paymentMethod,
              trackingNumber: orderData.trackingNumber,
              estimatedDelivery: new Date(new Date(orderData.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              history: orderData.history || [],
              prontoPagoDiscountAtOrderTime: orderData.prontoPagoDiscountAtOrderTime || 0,
              notes: orderData.notes || '',
              dropShipping: orderData.dropShipping || undefined
            };

            setOrder(formattedOrder);
          } else {
            console.error('Error al cargar detalles del pedido:', response.error);
          }
        } catch (error) {
          console.error('Error al cargar detalles del pedido:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchOrderDetail();
    }
  }, [auth.user, resolvedOrderId]);

  // Traducir el estado del pedido a español
  const getStatusText = (status: OrderDetail['status']) => {
    const statusMap = {
      pending: 'Pendiente',
      processing: 'En proceso',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado',
      payment_failed: 'Pago Fallido',
      refunded: 'Reembolsado'
    };
    return statusMap[status] || status;
  };

  // Obtener el color del estado
  const getStatusColor = (status: OrderDetail['status']) => {
    const colorMap = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      payment_failed: 'bg-red-100 text-red-800',
      refunded: 'bg-purple-100 text-purple-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  // Formatear fecha
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  if (!auth.isAuthenticated) {
    navigate('/login');
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-6">
        <Link
          to={backToOrders}
          onClick={() => trackingManager.events.track(
            EventType.CUSTOM,
            {
              eventName: 'order_detail_back',
              userId: auth.user?.id,
              orderId: resolvedOrderId
            }
          )}
          className="text-sm font-medium text-black hover:text-gray-700"
        >
          ← <T keyName="auth.order_detail.back_to_orders">Volver a mis pedidos</T>
        </Link>
      </div>

      {isLoading ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg p-8">
          <div className="animate-pulse flex flex-col space-y-6">
            <div className="h-6 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-3">
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded"></div>
              <div className="h-3 bg-gray-200 rounded"></div>
            </div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      ) : order ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  <T keyName="auth.order_detail.order_number" params={{ id: order.id }}>{`Pedido #${order.id}`}</T>
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  <T keyName="auth.order_detail.placed_on" params={{ date: formatDate(order.date) }}>{`Realizado el ${formatDate(order.date)}`}</T>
                </p>
              </div>
              <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                {getStatusText(order.status)}
              </span>
            </div>

            {/* Status Timeline - Shows for all active orders */}
            {['pending', 'processing', 'shipped', 'delivered'].includes(order.status) && (
              <div className="border-t border-gray-200 px-4 py-8 sm:px-6">
                <div className="relative">
                  <div className="absolute top-0 left-0 w-full h-10 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-between">
                    {/* Step 1: Pendiente */}
                    <div className="flex flex-col items-center">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center border-2 ${['pending', 'processing', 'shipped', 'delivered'].includes(order.status)
                        ? 'bg-black border-black'
                        : 'bg-white border-gray-300'
                        }`}>
                        <svg className={`h-6 w-6 ${['pending', 'processing', 'shipped', 'delivered'].includes(order.status) ? 'text-white' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className={`mt-2 text-sm font-medium ${['pending', 'processing', 'shipped', 'delivered'].includes(order.status) ? 'text-black' : 'text-gray-500'}`}>
                        <T keyName="auth.order_detail.received">Pendiente</T>
                      </span>
                    </div>

                    {/* Step 2: Procesando */}
                    <div className="flex flex-col items-center">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center border-2 ${['processing', 'shipped', 'delivered'].includes(order.status)
                        ? 'bg-black border-black'
                        : 'bg-white border-gray-300'
                        }`}>
                        <svg className={`h-6 w-6 ${['processing', 'shipped', 'delivered'].includes(order.status) ? 'text-white' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                      <span className={`mt-2 text-sm font-medium ${['processing', 'shipped', 'delivered'].includes(order.status) ? 'text-black' : 'text-gray-500'}`}>
                        <T keyName="auth.orders.processing">Procesando</T>
                      </span>
                    </div>

                    {/* Step 3: Enviado */}
                    <div className="flex flex-col items-center">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center border-2 ${['shipped', 'delivered'].includes(order.status)
                        ? 'bg-black border-black'
                        : 'bg-white border-gray-300'
                        }`}>
                        <svg className={`h-6 w-6 ${['shipped', 'delivered'].includes(order.status) ? 'text-white' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className={`mt-2 text-sm font-medium ${['shipped', 'delivered'].includes(order.status) ? 'text-black' : 'text-gray-500'}`}>
                        <T keyName="auth.orders.shipped">Enviado</T>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Tracking Info (only if processing/shipped/delivered) */}
                {(['processing', 'shipped', 'delivered'].includes(order.status)) && (
                  <div className="mt-8 bg-gray-50 p-4 rounded-md">
                    <div className="flex flex-col sm:flex-row justify-between">
                      {order.trackingNumber && (
                        <div className="mb-4 sm:mb-0">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider"><T keyName="auth.order_detail.tracking_number">Número de seguimiento</T></p>
                          <p className="mt-1 text-sm font-semibold text-gray-900">
                            {order.trackingUrl ? (
                              <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                {order.trackingNumber}
                              </a>
                            ) : (
                              order.trackingNumber
                            )}
                          </p>
                        </div>
                      )}
                      {order.estimatedDelivery && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider"><T keyName="auth.order_detail.estimated_delivery">Entrega estimada</T></p>
                          <p className="mt-1 text-sm font-semibold text-gray-900">{formatDate(order.estimatedDelivery)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Modificaciones (Cambios en el pedido) */}
            {order.history && order.history.some(h => h.details && (h.details.type === 'product_removed' || h.details.type === 'quantity_change')) && (
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6 bg-red-50">
                <h2 className="text-lg font-medium text-red-800 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <T keyName="auth.order_detail.modifications">Modificaciones del pedido</T>
                </h2>
                <ul className="divide-y divide-red-200">
                  {order.history
                    .filter(h => h.details && (h.details.type === 'product_removed' || h.details.type === 'quantity_change'))
                    .map((entry, idx) => (
                      <li key={idx} className="py-3 flex items-center justify-between">
                        <div className="flex items-center">
                          <div className={`p-2 rounded-full mr-3 ${entry.details.type === 'product_removed' ? 'bg-red-200' : 'bg-orange-200'}`}>
                            {entry.details.type === 'product_removed' ? (
                              <svg className="w-4 h-4 text-red-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-orange-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            )}
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${entry.details.type === 'product_removed' ? 'text-red-800 line-through' : 'text-orange-800'}`}>
                              {entry.details.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {entry.details.type === 'product_removed'
                                ? `Eliminado (${entry.details.previousQty} un.)`
                                : `Cantidad modificada: ${entry.details.from} -> ${entry.details.to}`}
                            </p>
                          </div>
                        </div>
                        <div className="group relative">
                          <span className="cursor-help text-red-500 hover:text-red-700">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </span>
                          <div className="absolute right-0 bottom-full mb-2 w-48 p-2 bg-black text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                            {entry.description}
                            <div className="absolute bottom-0 right-2 w-2 h-2 -mb-1 bg-black transform rotate-45"></div>
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            )}

            {/* Productos */}
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4"><T keyName="auth.order_detail.products">Productos</T></h2>
              <ul className="divide-y divide-gray-200">
                {order.items.map((item) => (
                  <li key={item.id} className="py-4 flex">
                    <div className="flex-shrink-0 h-24 w-24 bg-gray-100 rounded-md overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="h-full w-full object-center object-cover"
                      />
                    </div>
                    <div className="ml-4 flex-1 flex flex-col">
                      <div>
                        <div className="flex justify-between">
                          <h3 className="text-sm font-medium text-gray-900">
                            <Link to={`/products/${item.productId}`} className="hover:underline">
                              {item.name}
                            </Link>
                          </h3>
                          <div className="ml-4 text-right">
                            {(() => {
                              const pbd = item.priceBeforeDiscount || item.priceBeforeGlobalDiscount;
                              const dInfo = item.discountInfo || item.globalDiscountInfo;
                              if (pbd && pbd > item.price) {
                                return (
                                  <>
                                    <p className="text-sm text-gray-400 line-through">€{pbd.toFixed(2)}</p>
                                    <p className="text-sm font-medium text-green-600">€{(item.price || 0).toFixed(2)}</p>
                                    {dInfo && (
                                      <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                        {dInfo.displayValue}
                                      </span>
                                    )}
                                  </>
                                );
                              }
                              return <p className="text-sm font-medium text-gray-900">€{(item.price || 0).toFixed(2)}</p>;
                            })()}
                          </div>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">{item.variant}</p>
                      </div>
                      <div className="flex-1 flex items-end justify-between">
                        <p className="text-sm text-gray-500"><T keyName="auth.order_detail.quantity">Cantidad</T>: {item.quantity}</p>
                        <p className="text-sm font-medium text-gray-900"><T keyName="auth.order_detail.subtotal">Subtotal</T>: €{((item.price || 0) * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resumen */}
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <div className="sm:flex sm:justify-between">
                <div className="sm:w-1/2 mb-6 sm:mb-0">
                  <h2 className="text-lg font-medium text-gray-900 mb-4"><T keyName="auth.order_detail.shipping_address">Dirección de envío</T></h2>
                  <address className="not-italic text-sm text-gray-500">
                    <p>{order.shippingAddress.name}</p>
                    <p>{order.shippingAddress.address}</p>
                    <p>{order.shippingAddress.postalCode}, {order.shippingAddress.city}</p>
                    <p>{order.shippingAddress.country}</p>
                  </address>
                  {order.dropShipping?.enabled && order.dropShipping.recipient && (
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                        Destinatario final
                      </p>
                      <p className="mt-2 text-sm font-medium text-amber-900">{order.dropShipping.recipient.name}</p>
                      {order.dropShipping.recipient.email && (
                        <p className="text-sm text-amber-800">{order.dropShipping.recipient.email}</p>
                      )}
                      {order.dropShipping.recipient.phone && (
                        <p className="text-sm text-amber-800">{order.dropShipping.recipient.phone}</p>
                      )}
                      <p className="mt-2 text-sm text-amber-800">
                        Email logístico: {order.dropShipping.notifyRecipient ? 'Sí' : 'No'}
                      </p>
                    </div>
                  )}
                </div>
                <div className="sm:w-1/2 sm:pl-6">
                  <h2 className="text-lg font-medium text-gray-900 mb-4"><T keyName="auth.order_detail.payment_info">Información de pago</T></h2>
                  <p className="text-sm text-gray-500 mb-2">
                    <span className="font-medium"><T keyName="auth.order_detail.payment_method">Método de pago</T>:</span> {order.paymentMethod}
                  </p>
                  <div className="border-t border-gray-200 mt-4 pt-4">
                    <dl className="divide-y divide-gray-200">
                      <div className="py-2 flex justify-between">
                        <dt className="text-sm text-gray-500"><T keyName="auth.order_detail.subtotal">Subtotal</T></dt>
                        <dd className="text-sm font-medium text-gray-900">€{order.subtotal.toFixed(2)}</dd>
                      </div>
                      <div className="py-2 flex justify-between">
                        <dt className="text-sm text-gray-500"><T keyName="auth.order_detail.shipping">Envío</T></dt>
                        <dd className="text-sm font-medium text-gray-900">€{order.shipping.toFixed(2)}</dd>
                      </div>
                      <div className="py-2 flex justify-between font-medium">
                        <dt className="text-gray-900"><T keyName="auth.order_detail.total">Total</T></dt>
                        <dd className="text-black">€{order.total.toFixed(2)}</dd>
                      </div>
                      {order.prontoPagoDiscountAtOrderTime && order.prontoPagoDiscountAtOrderTime > 0 && (
                        <div className="py-2 flex justify-between bg-amber-50 px-2 rounded-md">
                          <dt className="text-sm text-amber-800">
                            <T keyName="auth.order_detail.pronto_pago">{`Con pronto pago (${order.prontoPagoDiscountAtOrderTime}%)`}</T>
                          </dt>
                          <dd className="text-sm font-semibold text-amber-800">€{(order.total * (1 - order.prontoPagoDiscountAtOrderTime / 100)).toFixed(2)}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Notas del pedido */}
            {order.notes && (
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                <h2 className="text-lg font-medium text-gray-900 mb-3"><T keyName="auth.order_detail.order_notes">Notas del pedido</T></h2>
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-md">
                  <p className="text-sm text-amber-900 whitespace-pre-line">{order.notes}</p>
                </div>
              </div>
            )}

            {/* Acciones */}
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <div className="flex justify-end space-x-3">
                {order.trackingUrl && (
                  <a
                    href={order.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <T keyName="auth.order_detail.track_shipment">Seguir envío</T>
                  </a>
                )}
                <button
                  type="button"
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-900"
                  onClick={() => {
                    if (!order) return;
                    const url = `/api/v1/orders/${order.id}/invoice.pdf`;
                    window.open(url, '_blank');
                  }}
                >
                  <T keyName="auth.order_detail.download_invoice">Descargar factura</T>
                </button>
              </div>
            </div>
          </div>

          {/* Atención al cliente */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4"><T keyName="auth.order_detail.need_help">¿Necesitas ayuda?</T></h2>
            <p className="text-sm text-gray-500 mb-4">
              <T keyName="auth.order_detail.need_help_message">Si tienes alguna pregunta o problema con tu pedido, no dudes en contactar con nuestro equipo de atención al cliente.</T>
            </p>
            <div className="flex">
              <Link
                to="/contact"
                className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <T keyName="auth.order_detail.contact_support">Contactar con soporte</T>
              </Link>
            </div>
          </div>
        </motion.div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="p-4 sm:p-6">
            <div className="text-center py-16">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900"><T keyName="auth.order_detail.order_not_found">Pedido no encontrado</T></h3>
              <p className="mt-1 text-sm text-gray-500"><T keyName="auth.order_detail.order_not_found_message">No hemos podido encontrar el pedido solicitado.</T></p>
              <div className="mt-6">
                <Link
                  to="/orders"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-black hover:bg-gray-900"
                >
                  <T keyName="auth.order_detail.view_my_orders">Ver mis pedidos</T>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailPage; 
